import { FocusEventHandler, Fragment, useState } from "react";
import PasswordTooltip from "./PasswordTooltip";
import Icon from "@/components/icons";

const PASSWORD_TYPES = ["new-password", "current-password"] as const;

type Props = {
  label: string;
  name: string;
  value?: string;
  autoComplete?:
    | "name"
    | "username"
    | "email"
    | "off"
    | (typeof PASSWORD_TYPES)[number];
  suggestions?: string[];
  validationError?: string;
  onChange?: (s: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  description?: string;
  isLogistrationV1?: boolean;
  isModal?: boolean;
};

export default function ValidatedField({
  label,
  name,
  suggestions,
  validationError,
  onChange,
  onBlur,
  value = "",
  description,
  autoComplete = "off",
  isLogistrationV1 = false,
  isModal = false,
}: Props) {
  const isPassword = PASSWORD_TYPES.includes(autoComplete as any);
  const hasSuggestions = suggestions && suggestions.length > 0;
  const [clearErrorInternally, setClearErrorInternally] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isFloating = isFocused || (value && value.trim().length > 0);

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const labelClass = isLogistrationV1
    ? "text-md font-bold text-gray-800"
    : `absolute left-4 transition-all duration-200 ease-in-out pointer-events-none z-10 bg-white text-sm
    ${
      isFloating
        ? "top-[-12px] text-xs text-primary-500"
        : "top-2 font-normal text-gray-500 mt-1"
    }`;

  const containerClass = isLogistrationV1 ? "relative" : "relative mb-7";
  const errorBorderClass = isLogistrationV1
    ? "border-[#AB0D02]"
    : "border-red-800";

  return (
    <div key={`form-field-${name}`} className={containerClass}>
      <label className={labelClass} htmlFor={name}>
        {label}
      </label>
      <div className="relative">
        <input
          id={name}
          type={isPassword ? (showPassword ? "text" : "password") : "text"}
          className={`w-full px-3 py-[9px] mb-0 border text-sm box-border focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
          ${validationError && !clearErrorInternally ? errorBorderClass : "border-gray-500"} ${isLogistrationV1 ? "rounded-[6px]" : ""}`}
          name={name}
          required
          autoComplete={autoComplete}
          onChange={(e) => {
            onChange?.(e.target.value);
          }}
          onBlur={handleBlur}
          onFocus={() => setIsFocused(true)}
          value={value}
          aria-label={label}
        />

        {/* Password toggle button for password field */}
        {isPassword && (
          <button
            type="button"
            className="absolute right-3 top-[9px] bottom-[9px] flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={togglePasswordVisibility}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <Icon
                type="visibility-off"
                svgProps={{ width: 24, height: 24 }}
              />
            ) : (
              <Icon type="visibility" svgProps={{ width: 24, height: 24 }} />
            )}
          </button>
        )}
      </div>
      {/* Password Requirements Tooltip */}
      {isPassword && isFocused && (
        <PasswordTooltip value={value} isModal={isModal} />
      )}
      <span
        className={`text-gray-500 text-xs mt-0.5 block transition-all duration-200 ease-in-out overflow-hidden whitespace-pre-line ${
          description && isFocused
            ? "opacity-100 max-h-10"
            : "opacity-0 max-h-0"
        }`}
      >
        {description}
      </span>
      <span
        className={`${isLogistrationV1 ? "text-[#AB0D02]" : "text-red-800"} text-xs mt-0.5 block transition-all duration-200 ease-in-out overflow-hidden ${
          (validationError || clearErrorInternally) && !isFocused
            ? "opacity-100 max-h-10"
            : "opacity-0 max-h-0"
        }`}
      >
        {validationError}
      </span>
      <span hidden={!hasSuggestions}>
        Suggestions:&nbsp;
        {suggestions?.map((s, idx) => {
          const isLast = idx === suggestions.length - 1;
          const spacer = isLast ? "." : ", ";
          return (
            <Fragment key={`suggestion-${s}`}>
              <a
                className="underline"
                onClick={() => {
                  onChange?.(s);
                  setClearErrorInternally(true);
                }}
              >
                {s}
              </a>
              {spacer}
            </Fragment>
          );
        })}
      </span>
    </div>
  );
}
